import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { CustomReportItem, ReportItem } from "types/Models/consolidatedReport";
import { VIEW_BY_OPTIONS } from "constants/reports";
import { PERIOD } from "constants/revenueMetrics";
import { FormikForm } from "components/FormikForm/FormikForm";
import { isCustomReport } from "utils/allReports";

type CustomReportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  report: CustomReportItem | null;
  onSave: (data: { groupBy: string; groupByLabel: string }) => void;
  modalTitle: string;
  submitText: string;
  isEditMode?: boolean;
  selectedReports?: ReportItem[];
};

type FormValues = {
  selectedValue: string;
  selectedLabel: string;
};

export const CustomReportModal = ({
  isOpen,
  onClose,
  report,
  onSave,
  modalTitle,
  submitText,
  isEditMode = false,
  selectedReports = [],
}: CustomReportModalProps) => {
  const usedGroupBys = selectedReports
    .filter(
      (item): item is CustomReportItem =>
        isCustomReport(item) &&
        item.custom_report_id === report?.custom_report_id
    )
    .map((item) => item.groupBy ?? "");

  const toOption = (label: string, value: string): OptionData => ({
    label,
    value,
    disableOption: usedGroupBys.includes(value),
    data: usedGroupBys.includes(value) ? "Added already" : undefined,
  });

  const noneOption = toOption("None", "");

  const periodGroup = [
    {
      label: "Period",
      options: VIEW_BY_OPTIONS.filter(
        ({ value }) => value !== PERIOD.TOTALS
      ).map(({ label, value }) => toOption(label, value)),
    },
  ];

  const groupByOptions = [noneOption, ...periodGroup];

  const allFlatOptions = [noneOption, ...periodGroup.flatMap((g) => g.options)];

  const allPossibleValues = [
    "",
    ...periodGroup.flatMap((g) => g.options.map((o) => o.value)),
  ];

  const nextAvailableValue =
    allPossibleValues.find((value) => !usedGroupBys.includes(value)) ?? "";

  let initialSelectedValue: string;
  if (isEditMode) {
    initialSelectedValue = report?.groupBy ?? "";
  } else {
    initialSelectedValue = nextAvailableValue;
  }

  const initialSelectedLabel =
    (allFlatOptions.find((opt) => opt.value === initialSelectedValue)
      ?.label as string) ??
    report?.groupByLabel ??
    "None";

  const initialValues: FormValues = {
    selectedValue: initialSelectedValue,
    selectedLabel: initialSelectedLabel,
  };

  const handleSubmit = (values: FormValues) => {
    onSave({
      groupBy: values.selectedValue,
      groupByLabel: values.selectedLabel,
    });
    onClose();
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="regular" useCustomOverlay asChild>
        <FormikForm initialValues={initialValues} onSubmit={handleSubmit}>
          {({ values, setFieldValue }) => {
            const selectedOption =
              allFlatOptions.find(
                (opt) => opt.value === values.selectedValue
              ) ?? noneOption;

            return (
              <>
                <Modal.Header>
                  <Modal.Title>{modalTitle}</Modal.Title>
                  <Modal.Close />
                </Modal.Header>

                <Modal.Body>
                  <Combobox
                    name="selectedValue"
                    label="Group by"
                    placeholder="None"
                    options={groupByOptions}
                    value={selectedOption}
                    onChange={(selected) => {
                      if (selected && "value" in selected) {
                        setFieldValue("selectedValue", selected.value);
                        setFieldValue(
                          "selectedLabel",
                          String(selected.label || "")
                        );
                      }
                    }}
                    block
                    withForm
                    menuPortalTarget={document.body}
                    isClearable={false}
                    tooltip
                    isOptionDisabled={(option) => Boolean(option.disableOption)}
                  />
                </Modal.Body>

                <Modal.FooterButtonGroup>
                  <Button customType="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button customType="primary" type="submit">
                    {submitText}
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
